import React, { useState, useEffect, useRef } from 'react';
import { Hash, ChevronDown, X, Plus, QrCode } from 'lucide-react';
import { BarcodeScanner } from './BarcodeScanner';

interface KonzernIdSelectorProps {
  value: string;
  onChange: (value: string) => void;
  availableIds: string[];
  currentId?: string;
  className?: string;
}

export const KonzernIdSelector: React.FC<KonzernIdSelectorProps> = ({ 
  value, 
  onChange,
  availableIds,
  currentId,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredIds, setFilteredIds] = useState<string[]>([]);
  const [showScanner, setShowScanner] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Update filtered IDs when available IDs change
  useEffect(() => {
    setFilteredIds(availableIds);
  }, [availableIds]);

  // Filter IDs based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredIds(availableIds);
    } else {
      setFilteredIds(
        availableIds.filter(id => 
          id.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
  }, [searchTerm, availableIds]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (id: string) => {
    onChange(id);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
  };

  const handleScanComplete = (scannedValue: string) => {
    // Check if the scanned value is in the available IDs
    if (availableIds.includes(scannedValue)) {
      onChange(scannedValue);
    } else {
      // If not in available IDs, still set it as the value
      // This allows for scanning new IDs that might not be in the system yet
      onChange(scannedValue);
      
      // You could also show a confirmation dialog here
      // or add the new ID to the available IDs list
    }
    
    setShowScanner(false);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div 
        className="flex items-center justify-between w-full p-2.5 border border-gray-300 rounded-lg cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2 flex-grow">
          <Hash className="h-5 w-5 text-gray-400" />
          {value ? (
            <div className="flex items-center justify-between w-full">
              <span className="text-gray-700">{value}</span>
              <button
                onClick={handleClear}
                className="p-1 hover:bg-gray-100 rounded-full"
                title="Konzern ID entfernen"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
          ) : (
            <span className="text-gray-500">Konzern ID auswählen</span>
          )}
        </div>
        <ChevronDown className="h-4 w-4 text-gray-500" />
      </div>

      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg">
          <div className="p-2">
            <div className="flex gap-2">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Konzern ID suchen..."
                className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                onClick={(e) => e.stopPropagation()}
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowScanner(true);
                  setIsOpen(false);
                }}
                className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                title="Barcode scannen"
              >
                <QrCode className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          <ul className="max-h-60 overflow-y-auto py-1">
            {filteredIds.length > 0 ? (
              filteredIds.map(id => (
                <li 
                  key={id}
                  className={`px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between ${
                    id === currentId ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => handleSelect(id)}
                >
                  <div className="flex items-center gap-2">
                    <Hash className="h-4 w-4 text-gray-400" />
                    <span>{id}</span>
                  </div>
                  {value === id && (
                    <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
                  )}
                </li>
              ))
            ) : (
              <li className="px-4 py-2 text-gray-500 text-center">
                Keine Konzern IDs gefunden
              </li>
            )}
          </ul>
          
          {currentId && !availableIds.includes(currentId) && (
            <div className="border-t border-gray-200 p-2">
              <div className="px-4 py-2 text-sm text-blue-600 bg-blue-50 rounded-md">
                <div className="font-medium">Aktuelle Konzern ID:</div>
                <div className="flex items-center gap-2 mt-1">
                  <Hash className="h-4 w-4 text-blue-500" />
                  <span>{currentId}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {showScanner && (
        <BarcodeScanner
          onScan={handleScanComplete}
          onClose={() => setShowScanner(false)}
          title="Konzern ID scannen"
          description="Scannen Sie den Barcode oder QR-Code der Konzern ID"
        />
      )}
    </div>
  );
};